import { AnimatePresence, motion } from 'framer-motion';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from '../../providers/ThemeProvider';

const ICON_VARIANTS = {
  initial: { opacity: 0, rotate: -60, scale: 0.6 },
  animate: { opacity: 1, rotate: 0, scale: 1 },
  exit: { opacity: 0, rotate: 60, scale: 0.6 }
};

/**
 * Toggle de thème. Utilise les CSS variables du design system pour rester
 * visible dans les 2 modes (contraste garanti via --border-subtle et --text-secondary).
 *
 * @param {{ variant?: 'light' | 'dark' | 'auto', className?: string }} props
 */
export function ThemeToggle({ variant = 'auto', className = '' }) {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';
  const label = isDark ? 'Passer au mode clair' : 'Passer au mode sombre';

  // Variantes :
  //  - 'auto'  : suit le thème via CSS vars — marche dans la sidebar
  //  - 'dark'  : bouton clair sur fond toujours sombre (pages auth)
  //  - 'light' : inverse
  const variantClasses =
    variant === 'dark'
      ? 'bg-white/10 hover:bg-white/20 text-white border border-white/20'
      : variant === 'light'
        ? 'bg-stone-100 hover:bg-stone-200 text-stone-700 border border-stone-200'
        : 'bg-[color:var(--surface-2)] hover:bg-[color:var(--border-subtle)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)] border border-[color:var(--border-subtle)]';

  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={label}
      title={label}
      className={`relative p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50 ${variantClasses} ${className}`}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={isDark ? 'sun' : 'moon'}
          variants={ICON_VARIANTS}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.18, ease: 'easeOut' }}
          className="block"
        >
          {isDark ? (
            <Sun size={16} aria-hidden="true" strokeWidth={2} />
          ) : (
            <Moon size={16} aria-hidden="true" strokeWidth={2} />
          )}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

export default ThemeToggle;
